import React, { useState } from 'react';
import { PlusCircleFilled } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { I18n } from '@aws-amplify/core';
import { Col, Row } from 'antd';
import moment from 'moment';

import { useApplicationContext } from '@/context/v1/Application/context';
import { Title } from '@/components/Typography';

import { DeleteJdsD86Request, FilterRequest } from './hooks/types/request';
import { JdsD86ListDTO } from './hooks/types/response';
import { useDeleteLastReviewJdsD86, useDeleteReportJdsD86, useJdsD86List } from './hooks/hooks';
import { Filter, FormFields } from './Filter';
import { ReportList } from './ReportList';
import { CustomButtonAntd } from './styles';
import { ModalReviewsState, SelectedReport } from './types';
import { ModalDelete } from './ModalDelete';
import { ModalReviews } from './ModalReviews';

export const EwaJdsD86Reports: React.FC = () => {
	const { search } = useLocation();
	const history = useHistory();
	const searchParams = new URLSearchParams(search);
	const { organization, company } = useApplicationContext();

	const queryParams = convertURLParamToObject(searchParams);

	const [page, setPage] = useState<number>(0);
	const [maxReports, setMaxReports] = useState<number>(10);
	const [filter, setFilter] = useState<FilterRequest>(queryParams);
	const [selectedReport, setSelectedReport] = useState<SelectedReport>();
	const [reviews, setReviews] = useState<ModalReviewsState>({
		open: false,
		id: '',
	});

	const initialValues: FormFields = mapFilterInitialValues(queryParams) ?? {};

	const { mutateAsync: deleteReport, isLoading: deletingReport } = useDeleteReportJdsD86();
	const { mutateAsync: deleteLasReview, isLoading: deletingLasReview } = useDeleteLastReviewJdsD86();

	const {
		data: jdsD86List,
		isLoading,
		isFetching
	} = useJdsD86List({
		custom_report_name: 'jds_d86',
		filter: {
			...filter,
			offset: page === 0 ? String(page) : String(page - 1),
			limit: String(maxReports)
		}
	});

	const dataSource: JdsD86ListDTO = jdsD86List;

	function handleClose() {
		setSelectedReport(undefined)
	}

	function handleOpenReviews(id: string) {
		setReviews({
			id,
			open: true,
		})
	}

	function handleCloseReviews() {
		setReviews({
			id: '',
			open: false,
		})
	}

	function handleOpen(report: SelectedReport) {
		setSelectedReport(report)
	}

	function handlePageChange(page: number) {
		setPage(page);
	}

	function handleMaxReportsChange(reports: number) {
		setMaxReports(reports);
	}

	function handleFilterClick(filterData: FilterRequest) {
		setFilter(filterData);
	}

	function mapFilterInitialValues(queryParams: FilterRequest) {
		if (Object.keys(queryParams)?.length > 0) {
			const { created_at_start, created_at_end, reviewed_at_start, reviewed_at_end } = queryParams;

			const created_at =
				created_at_start && created_at_end ? [moment(created_at_start), moment(created_at_end)] : undefined;
			const reviewed_at =
				reviewed_at_start && reviewed_at_end ? [moment(reviewed_at_start), moment(reviewed_at_end)] : undefined;
			const initialValues: FormFields = {
				...queryParams,
				created_at,
				reviewed_at
			};
			return initialValues;
		}
	}

	async function handleDeleteReport(id: string) {
		const body: DeleteJdsD86Request = {
			company_id: company?.id,
			organization_id: organization?.id,
			id
		};
		await deleteReport({ body });
		handleClose();
	}

	async function handleDeleteLastRevaluation(id: string) {
		const body: DeleteJdsD86Request = {
			company_id: company?.id,
			organization_id: organization?.id,
			id
		};
		await deleteLasReview({ body });
		handleClose();
	}

	function convertURLParamToObject(searchParams: URLSearchParams) {
		return Object.fromEntries(searchParams.entries());
	}

	function handleCreateJdsD86Click() {
		history.push('/custom-reports/jds-d86/report');
	}

	return (
		<>
			<Row gutter={[0, 10]} justify="center">
				<Col span={24}>
					<Row justify={'space-between'}>
						<Col>
							<Title level={4}>JDS-D86</Title>
						</Col>
						<Col span={0.1}>
							<CustomButtonAntd onClick={handleCreateJdsD86Click} type="primary" icon={<PlusCircleFilled />}>
								{I18n.get('New Report')}
							</CustomButtonAntd>
						</Col>
					</Row>
				</Col>
				<Col span={24}>
					<Filter loading={isFetching} onFilterClick={handleFilterClick} initialValues={initialValues} />
				</Col>
				<Col span={24}>
					<ReportList
						data={dataSource}
						page={page}
						maxReports={maxReports}
						onPageChange={handlePageChange}
						onDelete={handleOpen}
						onReviewsClick={handleOpenReviews}
						onMaxReportsChange={handleMaxReportsChange}
						isLoading={isLoading || isFetching || deletingLasReview || deletingReport}
					/>
				</Col>
			</Row>
			<ModalDelete
				report={selectedReport}
				open={!!selectedReport}
				onDeleteReport={handleDeleteReport}
				handleDeleteLastRevaluation={handleDeleteLastRevaluation}
				onCancel={handleClose} />
			<ModalReviews
				{...reviews}
				onCancel={handleCloseReviews}
				onSelect={console.log}
			/>
		</>
	);
};
